import { useCallback, useEffect, useState } from "react";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "useShellNavCollapse",
);

/** Before F-199 the choice was one browser-wide key; it seeds each admin. */
const LEGACY_STORAGE_KEY = "hc-admin-nav-collapsed";
export const getNavCollapsedStorageKey = (adminId: string) =>
  `${LEGACY_STORAGE_KEY}:${adminId}`;

const readStoredCollapsed = (adminId: string): boolean => {
  try {
    const stored =
      window.localStorage.getItem(getNavCollapsedStorageKey(adminId)) ??
      window.localStorage.getItem(LEGACY_STORAGE_KEY);
    return stored === "1";
  } catch {
    return false;
  }
};

const isEditableTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement && target.isContentEditable;

/**
 * F-199: the collapsed menu is remembered per admin, and ⌘B (Ctrl+B)
 * toggles it. Rich-text editors keep ⌘B for bold.
 */
export function useShellNavCollapse(adminId: string | null | undefined) {
  const key = adminId ?? "anonymous";
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const collapsed = overrides[key] ?? readStoredCollapsed(key);

  const toggle = useCallback(() => {
    const next = !collapsed;
    try {
      window.localStorage.setItem(
        getNavCollapsedStorageKey(key),
        next ? "1" : "0",
      );
    } catch {
      // Remembering the choice is best-effort.
    }
    logger.debug("Admin sidebar collapse toggled", {
      operation: "admin_shell_nav_collapse_toggle",
      collapsed: next,
    });
    setOverrides((current) => ({ ...current, [key]: next }));
  }, [collapsed, key]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        !(event.metaKey || event.ctrlKey) ||
        event.altKey ||
        event.key.toLowerCase() !== "b" ||
        isEditableTarget(event.target)
      ) {
        return;
      }
      event.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  return { collapsed, toggle };
}
